/**
 * /live/:token — Anonymous, read-only live ride tracker for family members.
 * No login required. Token is a signed JWT minted by the patient portal.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Car, Loader2, MapPin, Phone, ShieldAlert, User, Clock, Navigation } from "lucide-react";
import { API } from "@/lib/api";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function LiveTracker() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/nemt/rides/live/${token}`);
      const data = await res.json();
      if (!res.ok) {
        setState({ loading: false, error: data?.detail || "Unable to load ride", data: null });
        return;
      }
      setState({ loading: false, error: "", data });
    } catch (err) {
      setState({ loading: false, error: err.message, data: null });
    }
  };

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 15000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [token]);

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><Loader2 className="w-6 h-6 animate-spin text-brand-green" /></div>;
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg px-5">
        <div className="max-w-md text-center bg-white border border-brand-navy/10 rounded-2xl p-8" data-testid="live-error">
          <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto" />
          <h1 className="font-serif text-2xl text-brand-deep mt-4">Link no longer valid</h1>
          <p className="text-sm text-brand-muted mt-2">
            {state.error === "This tracking link has expired"
              ? "This tracking link has expired. Ask the passenger for a fresh one."
              : "This tracking link is invalid or has been revoked."}
          </p>
        </div>
      </div>
    );
  }

  const { ride, driver, eta } = state.data;
  const hasMap = driver && driver.last_location;

  return (
    <div className="min-h-screen bg-brand-bg pb-16" data-testid="live-tracker">
      <div className="bg-brand-deep text-white px-5 py-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">Live ride tracker</div>
          <h1 className="font-serif text-2xl mt-1">
            {ride.member_first_name ? `${ride.member_first_name}'s ride` : "Ride tracker"}
          </h1>
          <div className="flex items-center gap-2 text-sm text-white/80 mt-1">
            <Clock className="w-4 h-4" />
            <span>{ride.trip_date} · {ride.trip_time}</span>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6 space-y-4">
        {/* Status */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted font-semibold">Status</div>
          <div className="font-serif text-xl text-brand-deep mt-1">
            {ride.driver_ride_status
              ? (ride.driver_ride_status === "en_route" ? "On the way" : titleCase(ride.driver_ride_status))
              : titleCase(ride.status || "Scheduled")}
          </div>
          <div className="text-xs text-brand-muted mt-2 flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" /> Pickup: {ride.pickup}
          </div>
          <div className="text-xs text-brand-muted mt-1 flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 mt-0.5 text-brand-navy shrink-0" /> Destination: {ride.dropoff}
          </div>
        </div>

        {/* Driver */}
        {driver ? (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Driver</div>
              {eta && ["accepted", "en_route", "arrived"].includes(ride.driver_ride_status) && (
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="live-eta">
                  <Navigation className="w-3.5 h-3.5" />
                  {ride.driver_ride_status === "arrived" ? "At pickup" : `${eta.minutes} min${eta.minutes === 1 ? "" : "s"} away`}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              {driver.has_photo ? (
                <img src={`${API}/nemt/driver/photo/${driver.id}`} alt={driver.name} className="w-16 h-16 rounded-full object-cover border-2 border-brand-navy/10" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-brand-surfaceAlt flex items-center justify-center"><User className="w-6 h-6 text-brand-muted" /></div>
              )}
              <div className="flex-1">
                <div className="font-serif text-lg text-brand-deep">{driver.name}</div>
                <div className="text-sm text-brand-muted mt-1 flex items-center gap-2 flex-wrap">
                  <Car className="w-3.5 h-3.5" />
                  {[driver.car_year, driver.car_color, driver.car_make, driver.car_model].filter(Boolean).join(" ") || "Vehicle info on the way"}
                </div>
                {driver.car_tag && (<div className="text-xs font-mono text-brand-deep mt-1">Tag: <strong>{driver.car_tag}</strong></div>)}
              </div>
            </div>

            {hasMap && (
              <div className="mt-4 rounded-xl overflow-hidden border border-brand-navy/10" style={{ height: 320 }} data-testid="live-map">
                <MapContainer center={[driver.last_location.lat, driver.last_location.lng]} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                  <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[driver.last_location.lat, driver.last_location.lng]}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-semibold">{driver.name}</div>
                        <div className="text-xs">On the way</div>
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-6 text-center">
            <Clock className="w-8 h-8 text-brand-green mx-auto" />
            <p className="text-sm text-brand-muted mt-3">A driver hasn't been assigned yet. Check back closer to the trip time.</p>
          </div>
        )}

        <p className="text-xs text-center text-brand-muted mt-8">
          Auto-refreshes every 15 seconds · 1st Touch Medical Transport
        </p>
      </div>
    </div>
  );
}
